import { UserButton } from "@clerk/nextjs";
import { Box, Stack } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { forwardRef } from "react";
import { AuthUser } from "@/auth/helpers";
import { useUser } from "@/auth/useUser";
import { GREY } from "@/config/mui/colorPalette";
import { useResponsive } from "@/hooks/common/useResponsive";
import useProviderIndex from "@/hooks/visits/useProviderIndex";
import { getDefaultRoute } from "@/utils";
import InitialsAvatar from "../avatar/initialsAvatar";
import { CrossIcon, MenuHamburgerIcon } from "../icons";
import ImpersonationBar from "./impersonationBar";
import TopBarActions from "./topBarActions";
import TopBarWrapper from "./topBarWrapper";

type Props = {
  onToggleNav: () => void;
  user: AuthUser;
  isNavOpen: boolean;
};

export const TOP_BAR_HEIGHT = "56px";

const TopBar = forwardRef<HTMLDivElement, Props>(function TopBarNavigation(
  { onToggleNav, user, isNavOpen },
  ref
) {
  const { userMedspa } = useUser();
  const getProviderIndex = useProviderIndex();
  const mdUp = useResponsive("up", "md");

  const handleCloseNavBar = () => {
    if (isNavOpen) {
      onToggleNav();
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        width: "100%",
      }}
    >
      <ImpersonationBar />
      <TopBarWrapper>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignContent: "center",
            }}
          >
            {!mdUp && (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                }}
              >
                {isNavOpen ? (
                  <CrossIcon onClick={onToggleNav} color={GREY[50]} />
                ) : (
                  <MenuHamburgerIcon onClick={onToggleNav} color={GREY[50]} />
                )}
                <Box component={Link} href={getDefaultRoute(userMedspa)}>
                  <Image
                    src="/assets/moxie-logo.svg"
                    width={80}
                    height={20}
                    alt="Moxie logo"
                  />
                </Box>
              </Stack>
            )}
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <TopBarActions user={user} onCloseNavBar={handleCloseNavBar} />
            <Box
              // We want to use our own avatar, instead of Clerk's avatar
              // However since we cannot replace Clerk's avatar with our own
              // we overlay our own on top of Clerk's, and pass through click events
              sx={{
                display: "flex",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  left: 0,
                  bottom: 0,
                }}
              >
                <UserButton
                  appearance={{
                    elements: {
                      userButtonBox: {
                        opacity: 0,
                      },
                      userButtonAvatarImage: {
                        // Sometimes there's an ugly drag shadow when you accidentally
                        // drag Clerk's avatar, remove it (even if only on webkit browsers)
                        "-webkit-user-drag": "none",
                      },
                      userPreviewAvatarBox: {
                        display: "none",
                      },
                      userPreview: {
                        // remove the redundant gap between the now invisible, but technically still in the DOM
                        // avatar, and the user name
                        gap: 0,
                      },
                      userButtonPopoverActions: {
                        gap: 0,
                        "& > div": {
                          marginLeft: 0,
                          gap: "20px",
                        },
                      },
                      userPreviewMainIdentifier: {
                        fontWeight: 600,
                      },
                    },
                  }}
                  userProfileProps={{
                    appearance: {
                      elements: {
                        userPreviewAvatarBox: {
                          display: "none",
                        },
                        userPreview: {
                          // remove the redundant gap between the now invisible, but technically still in the DOM
                          // avatar, and the user name
                          gap: 0,
                        },
                        profileSectionPrimaryButton__profile: {
                          // hide the "Update Profile" button
                          display: "none",
                        },
                        profileSection__emailAddresses: {
                          "& .cl-menuButtonEllipsis": {
                            // hide the button next to email addresses, so users can't remove their email address
                            display: "none",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
              <InitialsAvatar
                size={28}
                initials={{
                  firstName: user.firstName,
                  lastName: user.lastName,
                  userIndex: getProviderIndex(user.id),
                }}
                sx={{
                  // pass through pointer events to the Clerk button
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Stack>
        </Stack>
      </TopBarWrapper>
    </Box>
  );
});

export default TopBar;
